import Debugger from './Classes/Debugger';
import Interface from './Classes/Interface';
import Translator from './Classes/Translator';
import Tag from './Classes/Tag';
import DefaultTag from './Classes/Tag/DefaultTag';
import GoogleTag from './Classes/Tag/GoogleTag';
import IFrameTag from './Classes/Tag/IFrameTag';
import ScriptTag from './Classes/Tag/ScriptTag';
import StyleTag from './Classes/Tag/StyleTag';
import Settings from "./Interfaces/Settings";
import Tags from "./Interfaces/Tags";
import Layout from "./Types/Layout";
import Theme from "./Types/Theme";

declare global {
    interface Window {
        ConsentManager: ConsentManager;
    }
}

/**
 * ConsentManager
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2026-06-30 Improved translator, TypeScript cleaned.
 * @package fk_js/consentmanager
 * @version 5.3.1
 */
class ConsentManager
{

    public static THEME_LIGHT                     :Theme  = 'theme-light';
    public static THEME_DARK                      :Theme  = 'theme-dark';
    public static LAYOUT_CENTER                   :Layout = 'center';
    public static LAYOUT_BOTTOM_LEFT              :Layout = 'bottom-left';
    public static LAYOUT_BOTTOM_WIDE              :Layout = 'bottom-wide';
    public static LAYOUT_BOTTOM_RIGHT             :Layout = 'bottom-right';
    public static LAYOUT_TOP_LEFT                 :Layout = 'top-left';
    public static LAYOUT_TOP_WIDE                 :Layout = 'top-wide';
    public static LAYOUT_TOP_RIGHT                :Layout = 'top-right';
    public static FINGERPRINT_LAYOUT_BOTTOM_LEFT  :Layout = 'bottom-left';
    public static FINGERPRINT_LAYOUT_BOTTOM_RIGHT :Layout = 'bottom-right';

    readonly isConstructed         :boolean = false;

    private debugger               :Debugger = (new Debugger).setIdentifier(this.constructor.name);
    private errors                 :string[] = [];
    private interface              ?:Interface;
    private settings               :Settings = {
        debug: false,
        disableFrames: true,
        interfaceTemplate: '<p class="consent-manager__header">{interface_header}</p><p class="consent-manager__text">{interface_text}</p>',
        interfaceTheme: ConsentManager.THEME_LIGHT as Theme,
        interfaceLayout: ConsentManager.LAYOUT_BOTTOM_LEFT,
        lifetime: 24 * 3600 * 1000,
        fingerprintIcon: true,
        fingerprintIconLayout: ConsentManager.FINGERPRINT_LAYOUT_BOTTOM_RIGHT,
        privacyUri: '/datenschutz',
        queuePageReloadDelay: 1000,
        useAcceptAll: true,
        useDenyAll: false
    };
    private queuePageReloadTimeout ?:ReturnType<typeof setTimeout>;
    private requireUserInteraction :boolean = false;
    private storageIdentifier      :string = 'ConsentManagerStorage';
    private tags                   :Tags = {};
    private translator             ?:Translator;

    /**
     * Consent manager constructor.
     */
    private constructor(options :Settings, tags :Tag[] = [], translations :null|object = null)
    {
        if (!this.validateOptions(options)) {
            return this;
        }
        this.mergeSettings(options)
            .loadTranslator(translations)
            .loadInterface()
            .addInitialTags(tags)
            .addEventHandlers();
        if (this.requireUserInteraction) {
            this.showInterface();
        }
        this.isConstructed = true;
        return this;
    }

    // ##############
    // Static methods
    // ##############

    /**
     * Await global consent manager instance.
     */
    static await(callback :Function) :void
    {
        if (!callback || Function !== callback.constructor) {
            return;
        }
        const waitForConsentManager = setInterval(() => {
            // TODO: What if the globally set ConsentManager is NOT stored in window.ConsentManager??
            // TODO: Either force window.ConsentManager on initialisation (constructor()) or make it dynamic via func arg
            if (window.ConsentManager) {
                clearInterval(waitForConsentManager);
                callback(window.ConsentManager);
            }
        }, 250);
    }

    /**
     * Initialization helper.
     */
    static init(options :Settings, tags :Tag[], translations ?:Record<string, Record<string, any>>) :ConsentManager
    {
        return new ConsentManager(options, tags, translations);
    }

    // ###############
    // Private methods
    // ###############

    /**
     * Validate options.
     */
    private validateOptions(options :Settings)
    {
        if (!options) {
            console.error('[ConsentManager]', 'No options argument passed to constructor');
            return false;
        }
        // TODO: validate more options
        return true;
    }

    /**
     * Merge options with default settings.
     */
    private mergeSettings(options :Settings) :ConsentManager
    {
        const invalidOptions :string[] = [];
        Object.keys(options).forEach((option :any) :void => {
            if (0 > Object.keys(this.settings).indexOf(option)) {
                invalidOptions.push(option);
                delete options[option as keyof Settings];
            }
        });
        this.settings = { ...this.settings, ...options };
        if (!this.settings.debug) {
            this.debugger.disable();
        }
        if (invalidOptions.length) {
            this.debugger.error('Invalid options:', invalidOptions);
        }
        this.debugger.log(this.settings);
        return this;
    }

    /**
     * Load Translator instance.
     */
    private loadTranslator(translations :null|object) :ConsentManager
    {
        this.translator = new Translator(translations || {});
        return this;
    }

    /**
     * Build consent manager interface.
     */
    private loadInterface() :ConsentManager
    {
        this.interface = new Interface(this);
        return this;
    }

    /**
     * Add the initial tags (tags passed to constructor).
     */
    private addInitialTags(tags :Tag[]) :ConsentManager
    {
        this.addTag(new DefaultTag({
            label: this.translator?.get('tag_default'),
        }), true);
        if (tags.length) {
            tags.forEach(tag => {
                this.addTag(tag, true);
            });
        }
        else if (!Object.values(this.getStorage()).length) {
            this.setRequireUserInteraction(true);
        }
        this.debugger.log(this.tags);
        return this;
    }

    /**
     * Add event handlers to buttons and components.
     */
    private addEventHandlers() :ConsentManager
    {
        this.interface?.getAcceptAll().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.updateTags(true);
        });

        this.interface?.getAcceptSelected().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.updateTags(false);
        });

        this.interface?.getDenyAll().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.updateTags(false, true);
        });

        this.addTrigger('[rel="consent-manager"]');

        window.addEventListener('click', (e :PointerEvent) :boolean|void => {
            if (e.target && (e.target as Element).getAttribute('rel') !== 'consent-manager') {
                return true;
            }
            if (null !== e.target.parentNode && 'consent-manager' !== e.target.parentNode.rel) {
                return true;
            }
            e.preventDefault();
            this.showInterface(e.target.dataset.consent);
        });

        return this;
    }

    /**
     * Update tags
     *
     * @param acceptAll
     * @param denyAll
     */
    private updateTags(acceptAll :Boolean = false, denyAll :Boolean = false) :void
    {
        const checkboxes :NodeListOf<HTMLInputElement>|undefined = this.interface?.getNode().querySelectorAll('input[type="checkbox"][name]:not([readonly])');
        let storeConsent :Record<string, any> = {};
        if (acceptAll) {
            // @ts-ignore
            for (const checkbox :HTMLInputElement of checkboxes) {
                checkbox.checked = true;
            }
        }
        else if (true === denyAll) {
            // @ts-ignore
            for (const checkbox :HTMLInputElement of checkboxes) {
                checkbox.checked = false;
            }
        }
        // @ts-ignore
        for (const checkbox :HTMLInputElement of checkboxes) {
            const tagName :string = checkbox.name.split('[')[1].split(']')[0]; // TODO: instead of a name="" attribute, just use dataset
            const tag :Tag = this.tags[tagName];
            storeConsent[tagName] = false;
            if (checkbox.checked) {
                storeConsent[tagName] = true;
                if (!tag.getConsent()) {
                    tag.allow();
                }
            }
            else if (!checkbox.checked && tag.getConsent()) {
                tag.disallow();
            }
        }
        try {
            const storeData = {
                consent: storeConsent,
                timestamp: Date.now().toString()
            };
            localStorage.setItem(this.storageIdentifier, JSON.stringify(storeData));
            this.settings.debug && console.log('ConsentManager.updatePropertes()', storeData);
        }
        catch (error :any) {
            this.errors.push(error);
            this.debugger.error(this.errors);
        }
        this.hideInterface();
        this.requireUserInteraction = false;
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
    }

    // ##############
    // Public methods
    // ##############

    /**
     * Add tag to consent manager.
     */
    public addTag(tag :Tag, onInitialisation :boolean = false) :ConsentManager
    {
        tag.init(this || null);
        // TODO: check if tag with name exists already
        this.tags[tag.getName()] = tag;
        this.debugger.log(tag);
        this.interface?.addTag(tag);
        return this;
    }

    /**
     * Add a trigger which shows the interface (by selector).
     */
    public addTrigger(selector :string) :ConsentManager
    {
        document.querySelectorAll(selector)?.forEach((trigger :Node) :void => {
            (trigger as HTMLElement).addEventListener('click', (e :PointerEvent) :void => {
                e.preventDefault();
                this.showInterface((trigger as HTMLElement).dataset.consent || null);
            });
        });
        return this;
    }

    /**
     * Register callback function for a tag.
     * The callback function will also be called right away.
     */
    public registerTagCallback(tag :string, callback :Function|null = null) :ConsentManager // TODO: maybe rename to addTagCallback() or move to Tag.addCallback()
    {
        if (!tag || 0 > Object.keys(this.tags).indexOf(tag)) {
            console.warn('ConsentManager.registerTagCallback()', `tag "${tag}" does not exist.`); // TODO: use Debugger
            return this;
        }
        if (!callback || Function !== callback.constructor) {
            console.warn('ConsentManager.registerTagCallback()', `Callback "${callback}" is not a function.`); // TODO: use Debugger
            return this;
        }
        this.tags[tag].setCallback(callback);
        this.settings.debug && console.log('ConsentManager.registerTagCallback()', tag, callback);
        callback(this.getTagConsent(tag));
        return this;
    }

    /**
     * Show interface and, optionally, highlight a tag.
     */
    public showInterface(highlightTagWithName :null|string = null) :ConsentManager
    {
        this.interface?.show();
        if (!highlightTagWithName) {
            return this;
        }
        this.interface?.highlightTagWithName(highlightTagWithName);
        return this;
    }

    /**
     * Hide interface.
     */
    public hideInterface() :ConsentManager
    {
        this.interface?.hide();
        return this;
    }

    /**
     * Check if the current instance only uses the default tag (required cookies).
     */
    public usesOnlyDefaultTag() :boolean
    {
        return 1 === Object.keys(this.tags).length && 'default' === Object.keys(this.tags)[0];
    }

    // TODO: move to ConsentManager.StyleTag
    public enableStyleTag(tagName :string) :void
    {
        const styleTags = document.querySelectorAll(`style[type="text/plain"][data-consent="${tagName}"], link[type="text/plain"][data-consent="${tagName}"]`);
        if (styleTags) {
            styleTags.forEach((styleTag) => {
                styleTag.parentNode?.removeChild(styleTag);
                styleTag.setAttribute('type', 'text/css');
                if (styleTag.hasAttribute('data-src')) {
                    styleTag.setAttribute('src', styleTag.getAttribute('data-src') || '');
                    styleTag.removeAttribute('data-src');
                }
                if (styleTag.hasAttribute('data-href')) {
                    styleTag.setAttribute('href', styleTag.getAttribute('data-href') || '');
                    styleTag.removeAttribute('data-href');
                }
                document.head.appendChild(styleTag);
                const styleTagName = styleTag.tagName.toLowerCase();
                this.settings.debug && console.log(`ConsentManager.enablestyleTag(${tagName})`, `Enabled \<${styleTagName}\> with attribute data-consent="${tagName}"`);
            });
        }
        else {
            this.settings.debug && console.warn(`ConsentManager.enablestyleTag(${tagName})`, `There is no style tag with attribute data-consent="${tagName}"`);
        }
    }

    // TODO: move to ConsentManager.IFrameTag
    public enableIFrame(tagName :string) :void
    {
        const iFrames = document.querySelectorAll(`iframe[data-src][data-consent="${tagName}"]`);
        if (iFrames) {
            iFrames?.forEach((iFrame :Node) :void => {
                (iFrame as HTMLIFrameElement).src = (iFrame as HTMLIFrameElement).dataset.src || '';
                (iFrame as HTMLIFrameElement).removeAttribute('data-src');
            });
        }
        else {
            this.settings.debug && console.warn(`ConsentManager.enableIFrame(${tagName})`, `There is no iframe with attribute data-consent="${tagName}"`);
        }
    }

    /**
     * Queue a page reload.
     */
    public queuePageReload() :ConsentManager
    {
        if (null === this.queuePageReloadTimeout) {
            this.settings.debug && console.warn(`ConsentManager.queuePageReload()`);
            let c = confirm(this.translator?.get('notice_page_reload'));
            if (c) {
                this.queuePageReloadTimeout = setTimeout(() => {
                    window.location.reload();
                }, this.settings.queuePageReloadDelay);
            }
        }
        return this;
    }

    // #######
    // Getters
    // #######

    /**
     * Get cookie domain without subdomain with leading.
     */
    public getCookieDomain() :string
    {
        return `.${window.location.hostname.replace(/^[^.]+\./g, '')}`;
    }

    /**
     * Getter for the debugger utility.
     */
    public getDebugger() :Debugger
    {
        return this.debugger;
    }

    /**
     * Get privacy uri.
     */
    public getPrivacyUri() :string
    {
        if (this.settings.privacyUri.constructor === Object) {
            const language :string = this.translator?.getLanguage() ?? 'en';
            return (this.settings.privacyUri as Record<string, any>)[language] || '/';
        }
        return this.settings.privacyUri as string;
    }

    /**
     * Get a certain setting.
     */
    public getSetting(settingKey :string) :any
    {
        return this.settings[settingKey as keyof Settings] || null;
    }

    /**
     * Get (local) storage and check if saved settings are still valid.
     */
    public getStorage() :{}
    {
        const storage :any = localStorage.getItem(this.storageIdentifier) ?
            JSON.parse(localStorage.getItem(this.storageIdentifier) as string) :
            {};
        if (
            Object !== storage.constructor ||
            !storage.hasOwnProperty('consent') ||
            !storage.hasOwnProperty('timestamp') ||
            Date.now() > (parseInt(storage.timestamp)) + (this.settings.lifetime)
        ) {
            this.setRequireUserInteraction(true);
            return {};
        }
        return storage;
    }

    /**
     * Get the consent status of tag by its name.
     */
    public getTagConsent(tagName :string) :boolean
    {
        if (!(tagName in this.tags)) {
            console.warn('ConsentManager.getTagConsent', `tag "${tagName}" does not exist`); // TODO: use Debugger
            return false;
        }
        return this.tags[tagName]?.getConsent();
    }

    /**
     * Get all active tags.
     */
    public getTags() :Tags
    {
        return this.tags;
    }

    /**
     * Getter for the translator instance.
     */
    public getTranslator() :Translator
    {
        return this.translator || new Translator;
    }

    // #######
    // Setters
    // #######

    /**
     * Set whether the consent manager requires an interaction.
     */
    public setRequireUserInteraction(requireUserInteraction :boolean) :ConsentManager
    {
        this.requireUserInteraction = requireUserInteraction;
        if (this.isConstructed && requireUserInteraction) {
            this.showInterface();
        }
        return this;
    }

    /**
     * Set the consent status for a tag by its name.
     */
    public setTagConsent(tagName :string, allow :boolean = false, reload :boolean = false) :ConsentManager
    {
        if (!(tagName in this.tags)) {
            console.warn('ConsentManager.setTagConsent', `tag "${tagName}" does not exist`); // TODO: use Debugger
            return this;
        }
        this.settings.debug && console.log('ConsentManager.setTagConsent', 'setTagConsent()', arguments); // TODO: use Debugger
        if (allow) {
            this.tags[tagName].allow();
        }
        else {
            this.tags[tagName].disallow();
        }
        // TODO: Requires an update of local storage
        // TODO: Also, does not seem to work ...
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
        if (reload) {
            this.queuePageReload();
        }
        return this;
    }
}

// Define namespaces
ConsentManager.Tag = Tag;
ConsentManager.Tag.GoogleTag = GoogleTag;
ConsentManager.Tag.IFrameTag = IFrameTag;
ConsentManager.Tag.ScriptTag = ScriptTag;
ConsentManager.Tag.StyleTag = StyleTag;

// Export
export default ConsentManager;
